import type { Simplify } from 'type-fest';

import {
	createEnumNormalizer,
	createJsonNormalizer,
	normalizeBoolean,
	normalizeNumber,
	normalizeString,
} from '@change-corgi/fcm/normalizers';

import type { FcmConfig, Merger, SimplifyWhenEqual } from './types';

type FactoryFcmConfigWithoutMerger<N extends string, T, D> = Simplify<
	Readonly<{ name: N } & Omit<FcmConfig<T, D>, 'name' | 'merger'>>
>;
type FactoryFcmConfigWithMerger<N extends string, T, D> = Simplify<
	Readonly<{ name: N; merger: Merger<SimplifyWhenEqual<T, D>> } & Omit<FcmConfig<T, D>, 'name' | 'merger'>>
>;

// using this approach to allow for intellisense completion
type JsonWithDefaultFn<N extends string, T> = {
	(defaultValue: T): {
		withMerger: (merger: Merger<T>) => {
			toConfig: () => FactoryFcmConfigWithMerger<N, T, T>;
		};
		toConfig: () => FactoryFcmConfigWithoutMerger<N, T, T>;
	};
	(defaultValue: undefined): {
		withMerger: (merger: Merger<T | undefined>) => {
			toConfig: () => FactoryFcmConfigWithMerger<N, T, undefined>;
		};
		toConfig: () => FactoryFcmConfigWithoutMerger<N, T, undefined>;
	};
};

type FactoryMethods<N extends string> = {
	asBoolean: () => {
		withDefault: (defaultValue: boolean) => {
			withMerger: (merger: Merger<boolean>) => {
				toConfig: () => FactoryFcmConfigWithMerger<N, boolean, boolean>;
			};
			toConfig: () => FactoryFcmConfigWithoutMerger<N, boolean, boolean>;
		};
	};
	asNumber: () => {
		withDefault: <DEF extends number | undefined>(
			defaultValue: DEF,
		) => { toConfig: () => FactoryFcmConfigWithoutMerger<N, number, DEF extends undefined ? undefined : number> };
	};
	asString: () => {
		withDefault: <DEF extends string | undefined>(
			defaultValue: DEF,
		) => { toConfig: () => FactoryFcmConfigWithoutMerger<N, string, DEF extends undefined ? undefined : string> };
	};
	asEnum: <T extends string>(
		values: readonly T[],
	) => {
		withDefault: <DEF extends T | undefined>(
			defaultValue: DEF,
		) => { toConfig: () => FactoryFcmConfigWithoutMerger<N, T, DEF extends undefined ? undefined : T> };
	};
	asJson: <T>(normalizeParsedValue: (parsedValue: unknown) => T) => {
		withDefault: JsonWithDefaultFn<N, T>;
	};
};

// eslint-disable-next-line max-lines-per-function
export function defineFcm<N extends string>(name: N): FactoryMethods<N> {
	return {
		asBoolean: () => ({
			withDefault: (defaultValue: boolean) => ({
				withMerger: (merger: Merger<boolean>) => ({
					toConfig: () =>
						({
							name,
							normalizer: normalizeBoolean,
							defaultValue,
							merger,
						}) as FactoryFcmConfigWithMerger<N, boolean, boolean>,
				}),
				toConfig: () => ({ name, normalizer: normalizeBoolean, defaultValue }),
			}),
		}),
		asNumber: () => ({
			withDefault: <DEF extends number | undefined>(defaultValue: DEF) => ({
				toConfig: () =>
					({ name, normalizer: normalizeNumber, defaultValue }) as FactoryFcmConfigWithoutMerger<
						N,
						number,
						DEF extends undefined ? undefined : number
					>,
			}),
		}),
		asString: () => ({
			withDefault: <DEF extends string | undefined>(defaultValue: DEF) => ({
				toConfig: () =>
					({
						name,
						normalizer: normalizeString,
						defaultValue,
					}) as FactoryFcmConfigWithoutMerger<N, string, DEF extends undefined ? undefined : string>,
			}),
		}),
		asEnum: <T extends string>(values: readonly T[]) => ({
			withDefault: <DEF extends T | undefined>(defaultValue: DEF) => ({
				toConfig: () =>
					({
						name,
						normalizer: createEnumNormalizer(values),
						defaultValue,
					}) as FactoryFcmConfigWithoutMerger<N, T, DEF extends undefined ? undefined : T>,
			}),
		}),
		asJson: <T>(normalizeParsedValue: (parsedValue: unknown) => T) => ({
			withDefault: (<DEF extends T | undefined>(defaultValue: DEF) => ({
				withMerger: (merger: Merger<T | DEF extends undefined ? undefined : T>) => ({
					toConfig: () =>
						({
							name,
							normalizer: createJsonNormalizer(normalizeParsedValue),
							defaultValue,
							merger,
						}) as FactoryFcmConfigWithMerger<N, T, DEF extends undefined ? undefined : T>,
				}),
				toConfig: () =>
					({
						name,
						normalizer: createJsonNormalizer(normalizeParsedValue),
						defaultValue,
					}) as FactoryFcmConfigWithoutMerger<N, T, DEF extends undefined ? undefined : T>,
			})) as JsonWithDefaultFn<N, T>,
		}),
	};
}
